// UserEditPage.jsx
import React from 'react'
import { injectIntl } from 'react-intl'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet-async'
import { useNavigate, useParams } from 'react-router-dom'

import ConfirmDialog from '../partials/ConfirmDialog'
import Edit from '../partials/UserComponents/Edit'
import Footer from '../partials/Footer'
import NavigationBar from '../partials/NavigationBar'
import StatusSave from '../partials/StatusSave'
import withAppStore from '../../util/withAppStore'

import styles from '../../../css/home.module.css'

import AppDispatcher from '../../dispatchers/AppDispatcher'
import AppStore from '../../stores/AppStore'

function UserEditPage (props) {
  const navigate = useNavigate()
  const { customerID, userID } = useParams()

  const [state, setState] = React.useState({
    name: undefined,
    confirmDeleteDialogVisible: false,
    statusMessageIsVisible: false,
    outcome: undefined,
    groups: undefined,
    firstName: undefined,
    lastName: undefined,
    email: undefined,
    language: undefined,
    level: undefined,
    user: undefined,
    userCustomer: undefined,
  })

  const backToUsers = React.useCallback(() => {
    AppDispatcher.dispatch({ action: 'unload-user-groups' })
    navigate(`/${customerID}/settings/users`)
  }, [customerID, navigate])

  const confirmDeleteUserAccess = React.useCallback(() => {
    setState(prevState => ({ ...prevState, confirmDeleteDialogVisible: true }))
  }, [])

  const deleteUserAccess = React.useCallback(() => {
    AppDispatcher.dispatch({
      action: 'update-user-customer',
      customerID,
      userID,
      data: { is_active: false },
      callback: handleDeleteResponse,
    })
    hideConfirmDeleteDialog()
  }, [customerID, userID])

  const getGroups = React.useCallback(() => {
    const sortString = (group) =>
      `${group.name}-${group.icon}-${group.deviceGroup}`

    const setUserGroups = () => {
      const userGroups = AppStore.getUserGroups()

      if (Array.isArray(userGroups)) {
        const rawGroups = userGroups
          .map((userGroup) => userGroup.group)
          .filter(Boolean)

        const seen = new Set()
        const uniqueGroups = rawGroups.filter((group) => {
          const key = group.id ?? group.name ?? group.deviceGroup
          if (!key) return true
          if (seen.has(key)) return false
          seen.add(key)
          return true
        })

        const groups = uniqueGroups
          .sort((groupA, groupB) =>
            sortString(groupA).localeCompare(sortString(groupB)))

        setState(prevState => ({ ...prevState, groups }))
      }
    }

    setUserGroups()

    const { id: customerIDFromStore } = AppStore.getCustomer() || {}
    const resolvedCustomerID = customerIDFromStore || customerID
    if (!resolvedCustomerID) {
      return
    }
    AppDispatcher.dispatch({
      action: 'get-user-groups',
      params: {
        all: true,
        customerID: resolvedCustomerID,
        includeGroup: true,
        userID,
      },
      callback: setUserGroups,
    })
  }, [userID])

  const getUserCustomer = React.useCallback(() => {
    AppDispatcher.dispatch({
      action: 'get-user-customer',
      customerID,
      userID,
      params: { includeUser: true },
      callback: () => {
        const { userCustomer } = AppStore.data
        if (userCustomer) {
          const {
            user,
            user: {
              name,
              first_name: firstName,
              last_name: lastName,
              email,
              locale: {
                language,
              },
            },
            permissions: {
              level,
            },
          } = userCustomer

          const fullName = firstName && firstName ? `${firstName} ${lastName}` : undefined
          const newState = {
            name: name || '',
            firstName: firstName || '',
            lastName: lastName || '',
            email,
            language,
            level,
            user,
            userCustomer,
            displayName: fullName || name || email,
          }

          setState(prevState => ({ ...prevState, ...newState }))
        }
      },
    })
  }, [customerID, userID])

  const handleChange = React.useCallback(() => {
    // forceUpdate equivalent - component will re-render when AppStore changes
  }, [])

  const handleDeleteResponse = React.useCallback((error) => {
    setState(prevState => ({
      ...prevState,
      statusMessageIsVisible: true,
      outcome: (error ? 'failure' : 'success'),
    }))

    if (error !== 'failure') {
      setTimeout(() => backToUsers(), 1000)
    }
  }, [backToUsers])

  const hideConfirmDeleteDialog = React.useCallback(() => {
    setState(prevState => ({ ...prevState, confirmDeleteDialogVisible: false }))
  }, [])

  React.useEffect(() => {
    window.scrollTo(0, 0)
    AppStore.addChangeListener(handleChange)
    getUserCustomer()
    getGroups()

    return () => {
      AppStore.removeChangeListener(handleChange)
      AppDispatcher.dispatch({ action: 'unload-user-change' })
    }
  }, [handleChange, getUserCustomer, getGroups])

  const {
    auth: {
      logout,
    },
    data: {
      user: {
        data: {
          id: currentUserID,
        },
      },
    },
    intl: {
      formatMessage,
    },
    pages: {
      UserEditPage: title,
    },
    route: {
      path,
    },
  } = props

  const {
    confirmDeleteDialogVisible,
    statusMessageIsVisible,
    outcome,
    groups,
    name,
    firstName,
    lastName,
    email,
    language,
    level,
    user,
    userCustomer,
  } = state

  const fullName = firstName && lastName ? `${firstName} ${lastName}` : undefined
  const displayName = fullName || name || email
  const canDelete = userID !== currentUserID

  return (
    <div>
      <Helmet><title>{title}</title></Helmet>
      <NavigationBar
        backFunc={backToUsers}
        logOut={() => logout(navigate)}
        name={displayName}
        page={path}
        rightControl={canDelete
          ? {
              icon: 'fa-trash',
              func: () => confirmDeleteUserAccess(),
            }
          : undefined}
      />
      <ConfirmDialog
        cancelCallback={hideConfirmDeleteDialog}
        confirmCallback={deleteUserAccess}
        confirmButtonText={`${formatMessage({
            id: 'general.delete',
            defaultMessage: 'DELETE',
            description: 'delete - confirm button text',
          })}`}
        title={`${formatMessage({
            id: 'general.warning',
            defaultMessage: 'WARNING',
            description: 'popup title',
          })}`}
        question={`${formatMessage({
            id: 'customer.users.deleteMessage',
            defaultMessage: 'Delete the User?',
            description: 'delete user message',
          }, {
            name: displayName,
          })}`}
        value={userID}
        visible={confirmDeleteDialogVisible}
      />
      {statusMessageIsVisible &&
        <StatusSave visible outcome={outcome} />}
      <div className={styles.pageWidth}>
        <div>
          <div className={styles.pageContainer}>
            {email
              ? (
                <Edit
                  userID={userID}
                  groups={groups}
                  name={name}
                  firstName={firstName}
                  lastName={lastName}
                  email={email}
                  language={language}
                  level={level}
                  user={user}
                  userCustomer={userCustomer}
                  getUserCustomer={getUserCustomer}
                />
                )
              : ''}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  )
}

UserEditPage.propTypes = {
  pages: PropTypes.object,
  data: PropTypes.object,
  auth: PropTypes.object,
  intl: PropTypes.object,
  route: PropTypes.object,
}

export default withAppStore(injectIntl(UserEditPage))
